import { api, API_BASE, ApiClientError } from './apiClient';
import type {
  SportsVodLibraryPage,
  SportsVodSportInfo,
  SportsVodSource,
  SportsVodTitle,
} from '@/types/sportsVod';
import type { FfrServer, FfrServersResponse } from '@/types/ffr';

export interface ListLibraryQuery {
  sport?: string;
  source?: SportsVodSource;
  q?: string;
  cursor?: string;
  limit?: number;
}

/**
 * Absolute URL for `<img>` poster elements. The backend's `/poster/:id`
 * route is intentionally anonymous (img tags can't carry Bearer headers)
 * and cuid-gated, mirroring the live-sports poster proxy.
 */
export function buildSportsVodPosterUrl(titleId: string): string {
  return `${API_BASE}/api/sports-vod/poster/${encodeURIComponent(titleId)}`;
}

export const sportsVodApi = {
  list(query: ListLibraryQuery = {}, signal?: AbortSignal): Promise<SportsVodLibraryPage> {
    const params = new URLSearchParams();
    if (query.sport) params.set('sport', query.sport);
    if (query.source) params.set('source', query.source);
    if (query.q && query.q.trim().length > 0) params.set('q', query.q.trim());
    if (query.cursor) params.set('cursor', query.cursor);
    if (typeof query.limit === 'number') params.set('limit', String(query.limit));
    const qs = params.toString();
    return api<SportsVodLibraryPage>(
      `/api/sports-vod/library${qs ? `?${qs}` : ''}`,
      { auth: true, signal: signal ?? null },
    );
  },

  listSports(signal?: AbortSignal): Promise<{ sports: SportsVodSportInfo[] }> {
    return api<{ sports: SportsVodSportInfo[] }>('/api/sports-vod/sports', {
      auth: true,
      signal: signal ?? null,
    });
  },

  getById(id: string, signal?: AbortSignal): Promise<SportsVodTitle> {
    return api<SportsVodTitle>(`/api/sports-vod/${encodeURIComponent(id)}`, {
      auth: true,
      signal: signal ?? null,
    });
  },

  /**
   * Servers×parts tree for a fullfightreplays title, or null when the feature
   * is unavailable: the backend route only exists when
   * STREAMING_FFR_CLIENT_MULTISERVER_ENABLED is on, so a 404 means "flag off /
   * not an FFR title" — the caller then falls back to the single-embed player.
   * Non-404 errors propagate so the caller can distinguish outage from off.
   */
  async getFfrServers(id: string, signal?: AbortSignal): Promise<FfrServer[] | null> {
    try {
      const res = await api<FfrServersResponse>(
        `/api/sports-vod-stream/${encodeURIComponent(id)}/servers`,
        { auth: true, signal: signal ?? null },
      );
      return res.servers;
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) return null;
      throw err;
    }
  },

  /**
   * The signed datacenter-proxy MP4 URL for an ok.ru fight part (native, ad-free
   * playback), or null when unavailable (404 = flag off / not an ok.ru part /
   * resolve failed) so the caller falls back to the ok.ru iframe. `part` is 1-based.
   */
  async getFfrOkruSource(id: string, part: number, signal?: AbortSignal): Promise<string | null> {
    try {
      const res = await api<{ url: string }>(
        `/api/sports-vod-stream/${encodeURIComponent(id)}/okru?part=${encodeURIComponent(part)}`,
        { auth: true, signal: signal ?? null },
      );
      return res.url ?? null;
    } catch (err) {
      if (err instanceof ApiClientError) return null;   // any API error → iframe fallback
      throw err;
    }
  },
};
